/// <reference types="vite/client" />
import {
  Outlet,
  createRootRoute,
  useNavigate,
  useRouter,
} from '@tanstack/react-router';
import { TanStackDevtools } from '@tanstack/react-devtools';
import { TanStackRouterDevtoolsPanel } from '@tanstack/react-router-devtools';
import { UserButton, useAuth } from '@clerk/react';
import { captureException } from '@sentry/react';
import { useEffect } from 'react';
import { Toaster } from 'sonner';
import { Loader2 } from 'lucide-react';
import { m } from '@/paraglide/messages.js';
import { getLocale } from '@/paraglide/runtime.js';
import { isExpectedError } from '@/instrument';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter } from '@/components/ui/card';

function RootError({ error }: { error: Error }) {
  const router = useRouter();
  useEffect(() => {
    if (!isExpectedError(error))
      captureException(error, { tags: { type: 'root_error' } });
  }, [error]);
  return (
    <div className="flex min-h-screen items-center justify-center">
      <Card className="w-full max-w-sm text-center">
        <CardContent className="space-y-1 pt-6">
          <p className="text-sm font-medium">{m.tutor_rootError_title()}</p>
          <p className="text-muted-foreground font-mono text-xs">
            {m.tutor_rootError_subtitle()}
          </p>
        </CardContent>
        <CardFooter className="justify-center">
          <Button variant="ghost" size="sm" onClick={() => router.invalidate()}>
            {m.tutor_rootError_retry()}
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
}

function NotFound() {
  const { isSignedIn, isLoaded } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isLoaded) return;
    void navigate({ to: '/', replace: true });
  }, [isLoaded, isSignedIn, navigate]);

  return (
    <div className="flex min-h-screen items-center justify-center">
      <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
    </div>
  );
}

export const Route = createRootRoute({
  errorComponent: RootError,
  component: RootComponent,
  notFoundComponent: NotFound,
});

function DevUserButton() {
  const { isSignedIn } = useAuth();
  if (!isSignedIn) return null;
  return (
    <div className="fixed top-3 right-3 z-50">
      <UserButton />
    </div>
  );
}

function RootComponent() {
  const locale = getLocale();
  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  return (
    <>
      <Outlet />
      {import.meta.env.DEV && <DevUserButton />}
      <Toaster richColors position="top-center" />
      {import.meta.env.DEV && (
        <TanStackDevtools
          config={{
            position: 'bottom-right',
          }}
          plugins={[
            {
              name: 'Tanstack Router',
              render: <TanStackRouterDevtoolsPanel />,
            },
          ]}
        />
      )}
    </>
  );
}
